import { collection, doc, setDoc, deleteDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { ProspectTag, ProspectTagType } from '../types/common';

/**
 * Add or update a prospect tag
 */
export const addProspectTag = async (
  userId: string, 
  placeId: string, 
  tagType: ProspectTagType,
  apartmentInfo: ProspectTag['apartmentInfo'],
  notes?: string
): Promise<void> => {
  try {
    const tagRef = doc(db, 'prospectTags', `${userId}_${placeId}`);
    const now = new Date();
    await setDoc(tagRef, {
      userId,
      placeId,
      tagType,
      apartmentInfo,
      notes: notes || '',
      taggedAt: now,
      lastUpdated: now
    });
  } catch (error) {
    console.error('Error adding prospect tag:', error);
    throw error;
  }
};

/**
 * Remove a prospect tag
 */
export const removeProspectTag = async (userId: string, placeId: string): Promise<void> => {
  try {
    const tagRef = doc(db, 'prospectTags', `${userId}_${placeId}`);
    await deleteDoc(tagRef);
  } catch (error) {
    console.error('Error removing prospect tag:', error);
    throw error;
  }
};

/**
 * Update a prospect tag type
 */
export const updateProspectTag = async (
  userId: string, 
  placeId: string, 
  newTagType: ProspectTagType,
  notes?: string
): Promise<void> => {
  try {
    const tagRef = doc(db, 'prospectTags', `${userId}_${placeId}`);
    const updateData: any = {
      tagType: newTagType,
      lastUpdated: new Date()
    };
    
    if (notes !== undefined) {
      updateData.notes = notes;
    }
    
    await updateDoc(tagRef, updateData);
  } catch (error) {
    console.error('Error updating prospect tag:', error);
    throw error;
  }
};

/**
 * Get all prospect tags for a user
 */
export const getUserProspectTags = async (userId: string): Promise<ProspectTag[]> => {
  try {
    const tagsRef = collection(db, 'prospectTags');
    const q = query(tagsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id, // Include the Firestore document ID
        userId: data.userId,
        placeId: data.placeId,
        tagType: data.tagType,
        apartmentInfo: data.apartmentInfo || {},
        notes: data.notes || '',
        taggedAt: data.taggedAt?.toDate() || new Date(),
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      };
    }) as ProspectTag[];
  } catch (error) {
    console.error('Error getting user prospect tags:', error);
    throw error;
  }
};

/**
 * Get all prospect tags (for overview purposes)
 */
export const getAllProspectTags = async (): Promise<ProspectTag[]> => {
  try {
    const tagsRef = collection(db, 'prospectTags');
    const querySnapshot = await getDocs(tagsRef);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        placeId: data.placeId,
        tagType: data.tagType,
        apartmentInfo: data.apartmentInfo || {},
        notes: data.notes || '',
        taggedAt: data.taggedAt?.toDate() || new Date(),
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      };
    }) as ProspectTag[];
  } catch (error) {
    console.error('Error getting all prospect tags:', error);
    throw error;
  }
};

/**
 * Check if a specific place is tagged by a user
 */
export const getProspectTag = async (userId: string, placeId: string): Promise<ProspectTag | null> => {
  try {
    const tagsRef = collection(db, 'prospectTags');
    const q = query(tagsRef, where('userId', '==', userId), where('placeId', '==', placeId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const tagData = querySnapshot.docs[0].data();
      return {
        id: querySnapshot.docs[0].id, // Include the Firestore document ID
        userId: tagData.userId,
        placeId: tagData.placeId,
        tagType: tagData.tagType,
        apartmentInfo: tagData.apartmentInfo || {},
        notes: tagData.notes || '',
        taggedAt: tagData.taggedAt?.toDate() || new Date(),
        lastUpdated: tagData.lastUpdated?.toDate() || new Date()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting prospect tag:', error);
    return null;
  }
};

/**
 * Get prospect tags filtered by tag type
 */
export const getProspectTagsByType = async (userId: string, tagType: ProspectTagType): Promise<ProspectTag[]> => {
  try {
    const tagsRef = collection(db, 'prospectTags');
    const q = query(tagsRef, where('userId', '==', userId), where('tagType', '==', tagType));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        userId: data.userId,
        placeId: data.placeId,
        tagType: data.tagType,
        apartmentInfo: data.apartmentInfo || {},
        notes: data.notes || '',
        taggedAt: data.taggedAt?.toDate() || new Date(),
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      };
    }) as ProspectTag[];
  } catch (error) {
    console.error('Error getting prospect tags by type:', error);
    throw error;
  }
};

/**
 * Update notes for a prospect tag
 */
export const updateProspectTagNotes = async (userId: string, placeId: string, notes: string): Promise<void> => {
  try {
    const tagRef = doc(db, 'prospectTags', `${userId}_${placeId}`);
    await updateDoc(tagRef, {
      notes,
      lastUpdated: new Date()
    });
  } catch (error) {
    console.error('Error updating prospect tag notes:', error);
    throw error;
  }
};
